import type { ReactNode } from 'react'
import cx from 'classnames'
import { Button } from '@/components/Button'
import { ContentError } from '@/components/ContentError'
import { Spinner } from '@/components/Spinner'
import { TabBar } from '@/components/TabBar'
import styles from './PreviewLayout.module.css'

export interface PreviewLayoutProps {
  inputs: ReactNode
  outputs: ReactNode
  testId?: string
}

/**
 * The preview: sample inputs on the left, what the engine computed from them
 * on the right.
 */
export function PreviewLayout({ inputs, outputs, testId }: PreviewLayoutProps) {
  return (
    <div className={styles.root} data-test={testId}>
      {inputs}
      {outputs}
    </div>
  )
}

export interface PreviewInputsPaneProps {
  children?: ReactNode
  /**
   * Whether a computation is running.
   */
  isPending?: boolean
}

/**
 * The pane with the sample inputs.
 */
export function PreviewInputsPane({
  children,
  isPending,
}: PreviewInputsPaneProps) {
  return (
    <section className={cx(styles.pane, styles.inputsPane)}>
      <header className={styles.paneHeader}>
        <span className={styles.paneTitle}>Inputs</span>
        <span className={cx(styles.spinner, isPending && styles.pending)}>
          <Spinner size={14} />
        </span>
      </header>
      <div className={styles.paneContent}>{children}</div>
    </section>
  )
}

export interface PreviewOutputsPaneProps {
  children?: ReactNode
  /**
   * `TabItem`s.
   */
  tabs: ReactNode
}

/**
 * The pane with the computed outputs.
 */
export function PreviewOutputsPane({
  children,
  tabs,
}: PreviewOutputsPaneProps) {
  return (
    <section className={cx(styles.pane, styles.outputsPane)}>
      <header className={styles.paneHeader}>
        <TabBar align="left">{tabs}</TabBar>
      </header>
      <div className={styles.paneContent}>{children}</div>
    </section>
  )
}

export interface PreviewInputSectionProps {
  title: string
  /**
   * Buttons that act on the section's inputs.
   */
  actions?: ReactNode
  children?: ReactNode
  testId?: string
}

/**
 * A group of sample inputs, under a header that stays in view.
 */
export function PreviewInputSection({
  title,
  actions,
  children,
  testId,
}: PreviewInputSectionProps) {
  return (
    <div className={styles.inputSection} data-test={testId}>
      <div className={styles.inputSectionHeader}>
        <span className={styles.inputSectionTitle}>{title}</span>
        <span className={styles.inputSectionActions}>{actions}</span>
      </div>
      <div className={styles.inputSectionContent}>{children}</div>
    </div>
  )
}

export interface PreviewComputeErrorProps {
  message: string
  /**
   * Offered when the engine found errors in the legend, which the editor
   * shows.
   */
  onGoToEditor?: () => unknown
  testId?: string
}

/**
 * Shown in place of the preview when the engine couldn't compute it.
 */
export function PreviewComputeError({
  message,
  onGoToEditor,
  testId,
}: PreviewComputeErrorProps) {
  return (
    <div className={styles.computeError} data-test={testId}>
      <ContentError
        size="lg"
        title="The preview couldn't be computed"
        error={message}
      />
      {onGoToEditor && (
        <>
          <p className={styles.computeErrorHelp}>
            Go back to the editor and fix the legend's errors.
          </p>
          <Button
            label="Go to editor"
            variant="primary"
            onPress={onGoToEditor}
            testId={testId && `${testId}-editor-button`}
          />
        </>
      )}
    </div>
  )
}
